import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Loader2, Server, SendHorizonal, Radio, Pencil, Trash2, Inbox, ChevronUp, ChevronDown, Zap } from "lucide-react";
import { SignaturePerMailbox } from "@/pages/SignaturePerMailbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const PROVIDERS = {
  Gmail: { imap: "imap.gmail.com", imapPort: 993, smtp: "smtp.gmail.com", smtpPort: 587 },
  Outlook: { imap: "outlook.office365.com", imapPort: 993, smtp: "smtp.office365.com", smtpPort: 587 },
  Aruba: { imap: "imaps.aruba.it", imapPort: 993, smtp: "smtps.aruba.it", smtpPort: 465 },
  Libero: { imap: "imapmail.libero.it", imapPort: 993, smtp: "smtp.libero.it", smtpPort: 465 },
  TIM: { imap: "imap.tim.it", imapPort: 993, smtp: "smtp.tim.it", smtpPort: 587 },
  PEC: { imap: "imaps.pec.aruba.it", imapPort: 993, smtp: "smtps.pec.aruba.it", smtpPort: 465 },
  Exchange: { imap: "outlook.office365.com", imapPort: 993, smtp: "smtp.office365.com", smtpPort: 587 },
  IMAP: { imap: "", imapPort: 993, smtp: "", smtpPort: 587 },
};

const CodeTag = ({ children }) => (
  <code className="font-mono text-[11px] bg-secondary px-1 py-0.5 rounded">{children}</code>
);

// Guida a fisarmonica per collegare Gmail via IMAP/SMTP con la "password per le app".
const GmailAppPasswordGuide = () => {
  const [open, setOpen] = useState(true);
  const steps = [
    {
      t: "Attiva la Verifica in due passaggi (2FA)",
      d: (<>Serve per poter generare la password dedicata. Vai su <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="text-[#007BFF] underline">Account Google → Sicurezza</a> e attiva la <strong>Verifica in due passaggi</strong>. Se è già attiva, prosegui.</>),
    },
    {
      t: "Genera una «Password per le app»",
      d: (<>Apri <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[#007BFF] underline">myaccount.google.com/apppasswords</a>, dai un nome (es. «Sedio») e clicca <strong>Crea</strong>. Google mostra una password di <strong>16 caratteri</strong>: copiala e usala qui sotto <strong>senza spazi</strong> (non sarà più rimostrata).</>),
    },
    {
      t: "Verifica che l'IMAP sia attivo",
      d: (<>Sugli account Gmail attuali l'IMAP è <strong>già attivo</strong>. Solo su caselle molto vecchie o Workspace: Gmail → Impostazioni → «Inoltro e POP/IMAP» → <strong>Attiva IMAP</strong>.</>),
    },
    {
      t: "Incolla email e password qui sotto",
      d: (<>Inserisci il tuo indirizzo Gmail e la <strong>password per le app</strong> (non quella normale). I server sono già compilati: IMAP <CodeTag>imap.gmail.com:993</CodeTag>, SMTP <CodeTag>smtp.gmail.com:587</CodeTag>. Poi premi «Connetti e importa».</>),
    },
  ];
  return (
    <div className="rounded-md border border-[#007BFF]/30 bg-[#007BFF]/[0.04]" data-testid="gmail-guide">
      <button type="button" data-testid="gmail-guide-toggle" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Mail className="h-4 w-4 text-[#007BFF]" />Come collegare Gmail in 4 passi (password per le app)
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0 space-y-3" data-testid="gmail-guide-steps">
          <p className="text-xs text-muted-foreground">Colleghi Gmail come una normale casella IMAP/SMTP, con una password dedicata generata da Google. Attivazione immediata, nessuna verifica Google richiesta.</p>
          <ol className="space-y-2.5">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`gmail-guide-step-${i + 1}`}>
                <span className="shrink-0 h-6 w-6 rounded-full bg-[#007BFF] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div className="text-[13px] leading-relaxed">
                  <div className="font-semibold text-foreground">{s.t}</div>
                  <div className="text-muted-foreground">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded px-2.5 py-1.5">
            Attenzione: va inserita la <strong>password per le app</strong>, non la password con cui accedi a Gmail dal browser. La password per le app <strong>non scade</strong>, quindi la connessione resta stabile nel tempo.
          </div>
        </div>
      )}
    </div>
  );
};

export default function Connetti() {
  const navigate = useNavigate();
  const [imap, setImap] = useState({
    provider: "Gmail", email_address: "", password: "",
    imap_host: "imap.gmail.com", imap_port: 993,
    smtp_host: "smtp.gmail.com", smtp_port: 587, from_name: "", limit: 0,
  });
  const [imapBusy, setImapBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [pushInfo, setPushInfo] = useState({ accounts: [], idle_supported: true });
  const [accounts, setAccounts] = useState([]);
  const [editAcc, setEditAcc] = useState(null);
  const [editForm, setEditForm] = useState({ email_address: "", password: "", imap_host: "", imap_port: 993, smtp_host: "", smtp_port: 587 });
  const [savingEdit, setSavingEdit] = useState(false);
  const [delAcc, setDelAcc] = useState(null);
  const [studioConfirmed, setStudioConfirmed] = useState(true);

  const loadAccounts = useCallback(() => api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {}), []);
  const loadPush = useCallback(() => api.get("/imap/push/status").then((r) => setPushInfo(r.data)).catch((e) => console.debug("push status failed", e)), []);

  const moveAccount = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= accounts.length) return;
    const next = [...accounts];
    [next[index], next[target]] = [next[target], next[index]];
    setAccounts(next);
    try { await api.put("/accounts/reorder", { ids: next.map((a) => a.id) }); }
    catch { toast.error("Impossibile aggiornare l'ordine"); loadAccounts(); }
  };

  useEffect(() => {
    api.get("/smtp/config").then((r) => {
      if (r.data.configured) {
        setSmtpConfigured(true);
        setImap((s) => ({ ...s, smtp_host: r.data.host || s.smtp_host, smtp_port: r.data.port || s.smtp_port, from_name: r.data.from_name || s.from_name }));
      }
    });
    loadPush();
    loadAccounts();
    api.get("/trial/status").then((r) => setStudioConfirmed(!(r.data && r.data.show_timer))).catch(() => setStudioConfirmed(true));
    const id = setInterval(loadPush, 5000);
    if (new URLSearchParams(window.location.search).get("gmail") === "connected") {
      toast.success("Account Gmail collegato! Importazione delle email in corso…");
      if (new URLSearchParams(window.location.search).get("demo_purged") === "1") {
        setTimeout(() => toast.info("Dati demo rimossi: ora vedi i tuoi dati reali."), 600);
      }
      window.history.replaceState({}, "", "/connetti");
      loadAccounts();
    }
    return () => clearInterval(id);
  }, [loadPush, loadAccounts]);

  const pickProvider = (p) => {
    const cfg = PROVIDERS[p];
    setImap((s) => ({ ...s, provider: p, imap_host: cfg.imap || s.imap_host, imap_port: cfg.imapPort || s.imap_port, smtp_host: cfg.smtp || s.smtp_host, smtp_port: cfg.smtpPort || s.smtp_port }));
  };

  const togglePush = async (acc) => {
    try {
      await api.post(`/imap/push/${acc.push_running ? "disable" : "enable"}`, { account_id: acc.id });
      toast.success(acc.push_running ? "Sincronizzazione push disattivata" : "Sincronizzazione push attivata");
      setTimeout(loadPush, 800);
    } catch { toast.error("Errore nell'aggiornamento del push"); }
  };

  const fmtSince = (iso) => {
    if (!iso) return "mai";
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return "pochi secondi fa";
    if (s < 3600) return `${Math.floor(s / 60)} min fa`;
    if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
    return `${Math.floor(s / 86400)} g fa`;
  };

  const pushMeta = (a) => {
    const isGmail = a.type === "gmail_oauth";
    const st = a.push_state || (isGmail ? "connesso" : (a.push_running ? "connesso" : "disattivato"));
    if (["connesso", "polling", "attivo"].includes(st))
      return { label: isGmail ? "Connesso (automatico)" : "Connesso", dot: "bg-emerald-500", text: "text-emerald-700", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", pulse: false };
    if (["attivazione", "riconnessione"].includes(st))
      return { label: st === "riconnessione" ? "In riconnessione…" : "Attivazione…", dot: "bg-amber-500", text: "text-amber-700", badge: "bg-amber-50 text-amber-700 border-amber-200", pulse: true };
    if (st === "disattivato")
      return { label: "Disattivato", dot: "bg-slate-400", text: "text-slate-600", badge: "bg-slate-50 text-slate-600 border-slate-200", pulse: false };
    return { label: "Errore", dot: "bg-red-500", text: "text-red-700", badge: "bg-red-50 text-red-700 border-red-200", pulse: false };
  };

  const runVerify = async () => (await api.post("/imap/verify", imap)).data || {};

  const connect = async (e) => {
    e.preventDefault();
    if (!imap.email_address || !imap.password || !imap.imap_host)
      return toast.error("Compila indirizzo, password e host di ricezione (IMAP).");
    setImapBusy(true);
    try {
      // Auto-verifica prima del salvataggio: non salviamo mai una casella che non funziona
      const v = await runVerify();
      const imapOk = v.imap?.ok;
      const smtpOk = v.smtp?.ok; // true | false | null (host non indicato)
      if (!imapOk || smtpOk === false) {
        const msgs = [];
        if (!imapOk) msgs.push(`Ricezione (IMAP): ${v.imap?.message || "errore"}`);
        if (smtpOk === false) msgs.push(`Invio (SMTP): ${v.smtp?.message || "errore"}`);
        toast.error(`Verifica non superata: la casella NON è stata salvata. ${msgs.join(" · ")}`, { duration: 15000 });
        return;
      }
      if (v.smtp?.fallback) toast.info(v.smtp.message, { duration: 10000 });
      const r = await api.post("/imap/connect", imap);
      if (r.data.smtp_saved) setSmtpConfigured(true);
      toast.success(`Connessione verificata e riuscita: importazione di ${r.data.to_import} email in corso…`);
      if (r.data.demo_purged) setTimeout(() => toast.info("Dati demo rimossi: ora vedi i tuoi dati reali."), 600);
      // Vai subito alla Posta: le email arrivano lì mentre l'import prosegue in background
      navigate("/inbox");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connessione fallita");
    } finally { setImapBusy(false); }
  };

  const openEdit = (a) => {
    setEditAcc(a);
    setEditForm({ email_address: a.email_address || "", password: "", imap_host: a.imap_host || "", imap_port: a.imap_port || 993, smtp_host: imap.smtp_host || "", smtp_port: imap.smtp_port || 587 });
  };

  const saveEdit = async () => {
    if (!editAcc) return;
    setSavingEdit(true);
    const payload = { email_address: editForm.email_address, imap_host: editForm.imap_host, imap_port: +editForm.imap_port };
    if (editForm.password) payload.password = editForm.password;
    if (editForm.smtp_host) { payload.smtp_host = editForm.smtp_host; payload.smtp_port = +editForm.smtp_port; }
    try {
      await api.put(`/accounts/${editAcc.id}`, payload);
      toast.success("Casella aggiornata");
      setEditAcc(null);
      loadAccounts(); loadPush();
    } catch (err) { toast.error(err.response?.data?.detail || "Aggiornamento fallito"); }
    finally { setSavingEdit(false); }
  };

  const confirmDelete = async () => {
    if (!delAcc) return;
    const acc = delAcc;
    setDelAcc(null);
    const tid = toast.loading("Sto eliminando le email della casella…", { duration: Infinity });
    try {
      const r = await api.delete(`/accounts/${acc.id}`);
      loadAccounts(); loadPush();
      // Segue l'avanzamento della pulizia in background finché non sono state rimosse tutte le email.
      for (let i = 0; i < 90; i++) {
        await new Promise((res) => setTimeout(res, 1200));
        try {
          const s = await api.get(`/accounts/${acc.id}/purge-status`);
          if (s.data.done) break;
          toast.loading(`Sto eliminando le email della casella… (${s.data.remaining} rimaste)`, { id: tid, duration: Infinity });
        } catch { break; }
      }
      toast.success(`Casella ${r.data.email} eliminata (${r.data.deleted_communications} email rimosse)`, { id: tid, duration: 4000 });
      loadAccounts();
    } catch {
      toast.error("Eliminazione fallita", { id: tid, duration: 4000 });
    }
  };

  const [resyncing, setResyncing] = useState(false);
  const [reimporting, setReimporting] = useState(null);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const analyzeAll = async () => {
    setAnalyzingAll(true);
    try {
      const r = await api.post("/communications/analyze-all");
      toast.success(r.data.total ? `Analisi avviata su ${r.data.total} email` : (r.data.message || "Nessuna email da analizzare"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analisi fallita", { duration: 8000 });
    } finally { setAnalyzingAll(false); }
  };
  const reimportAccount = async (a) => {
    setReimporting(a.id);
    try {
      const r = await api.post(`/accounts/${a.id}/reimport`);
      toast.success(r.data.message || "Re-importazione avviata — le email arrivano in background");
      setTimeout(loadAccounts, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-importazione fallita", { duration: 8000 });
    } finally { setReimporting(null); }
  };
  const resyncGmail = async () => {
    setResyncing(true);
    try {
      const r = await api.post("/gmail/resync");
      toast.success(r.data.message || "Sincronizzazione avviata");
      setTimeout(loadAccounts, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sincronizzazione Gmail fallita", { duration: 10000 });
    } finally { setResyncing(false); }
  };

  const verifyMailbox = async () => {
    if (!imap.email_address || !imap.password || !imap.imap_host)
      return toast.error("Compila indirizzo, password e host di ricezione (IMAP) prima di provare.");
    setVerifying(true);
    try {
      const d = await runVerify();
      const imapOk = d.imap?.ok;
      const smtpOk = d.smtp?.ok; // true | false | null (host non indicato)
      if (imapOk && (smtpOk === true || smtpOk === null)) {
        if (d.smtp?.fallback) toast.success(`Ricezione (IMAP) OK. ${d.smtp.message}`, { duration: 12000 });
        else if (smtpOk === null) toast.success("Ricezione (IMAP) verificata. Nessun host SMTP indicato: aggiungilo per verificare anche l'invio.", { duration: 10000 });
        else toast.success("Connessione verificata: ricezione (IMAP) e invio (SMTP) funzionano correttamente. Ora puoi connettere la casella.", { duration: 9000 });
      } else {
        const msgs = [];
        if (!imapOk) msgs.push(`Ricezione (IMAP): ${d.imap?.message || "errore"}`);
        if (smtpOk === false) msgs.push(`Invio (SMTP): ${d.smtp?.message || "errore"}`);
        toast.error(msgs.join(" · ") || "Verifica fallita", { duration: 14000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verifica fallita");
    } finally { setVerifying(false); }
  };

  return (
    <div className="fade-in">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Ingestione</div>
        <h1 className="font-head text-4xl font-extrabold tracking-tighter">Connetti Account</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Collega Gmail, Outlook, Exchange, IMAP o PEC via app-password. Indirizzo e password valgono sia per la ricezione che per l'invio.</p>
      </header>

      <div className="p-4 sm:p-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Colonna sinistra: aggiunta di una nuova casella */}
        <div className="space-y-6">
        {/* Unified account box: stesso account per ricezione (IMAP) e invio (SMTP) */}
        <div className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="h-5 w-5 text-[#007BFF]" />
            <h3 className="font-head text-lg font-semibold">Account Email / PEC (IMAP)</h3>
            {smtpConfigured && <span className="text-[10px] uppercase tracking-wider text-emerald-600 border border-emerald-200 bg-emerald-50 rounded px-2 py-0.5">Invio configurato</span>}
          </div>
          <p className="text-sm text-muted-foreground mb-6 max-w-2xl">Inserisci una sola volta indirizzo e password: verranno usati sia per la <span className="font-medium text-foreground">ricezione</span> (IMAP) che per l'<span className="font-medium text-foreground">invio</span> (SMTP).</p>

          <form onSubmit={connect} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Provider</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {Object.keys(PROVIDERS).map((p) => (
                  <button type="button" key={p} data-testid={`imap-preset-${p}`} onClick={() => pickProvider(p)}
                    className={`text-xs px-2.5 py-1 rounded-md border transition-colors duration-200 ${imap.provider === p ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border text-muted-foreground hover:border-foreground/40"}`}>{p}</button>
                ))}
              </div>
              {imap.provider === "PEC" && (
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="pec-hint">Preset <strong>PEC Aruba</strong> (imaps.pec.aruba.it / smtps.pec.aruba.it). Per altri gestori PEC (Legalmail/InfoCert, Register, Poste) modifica gli host qui sotto.</p>
              )}
            </div>

            {imap.provider === "Gmail" && <GmailAppPasswordGuide />}
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Indirizzo email</Label>
                <Input data-testid="imap-email" type="email" value={imap.email_address} onChange={(e) => setImap({ ...imap, email_address: e.target.value })} placeholder="nome@azienda.it" className="rounded-md mt-1.5" required /></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Password / App-password</Label>
                <PasswordInput data-testid="imap-password" value={imap.password} onChange={(e) => setImap({ ...imap, password: e.target.value })} className="rounded-md mt-1.5" required /></div>
            </div>

            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome mittente (per l'invio)</Label>
              <Input data-testid="smtp-fromname" value={imap.from_name} onChange={(e) => setImap({ ...imap, from_name: e.target.value })} placeholder="Studio Rossi Amministrazioni" className="rounded-md mt-1.5" /></div>

            {/* RICEZIONE */}
            <div className="pt-2">
              <div className="flex items-center gap-2 mb-2.5"><Server className="h-4 w-4 text-[#007BFF]" /><h4 className="text-sm font-bold uppercase tracking-wider text-foreground">Ricezione (IMAP)</h4></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Host IMAP</Label>
                  <Input data-testid="imap-host" value={imap.imap_host} onChange={(e) => setImap({ ...imap, imap_host: e.target.value })} className="rounded-md mt-1.5" required /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Porta</Label>
                  <Input data-testid="imap-port" type="number" value={imap.imap_port} onChange={(e) => setImap({ ...imap, imap_port: +e.target.value })} className="rounded-md mt-1.5" /></div>
              </div>
            </div>

            {/* INVIO */}
            <div className="pt-1">
              <div className="flex items-center gap-2 mb-2.5"><SendHorizonal className="h-4 w-4 text-[#007BFF]" /><h4 className="text-sm font-bold uppercase tracking-wider text-foreground">Invio (SMTP)</h4></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Host SMTP</Label>
                  <Input data-testid="smtp-host" value={imap.smtp_host} onChange={(e) => setImap({ ...imap, smtp_host: e.target.value })} placeholder="smtp.gmail.com" className="rounded-md mt-1.5" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Porta</Label>
                  <Input data-testid="smtp-port" type="number" value={imap.smtp_port} onChange={(e) => setImap({ ...imap, smtp_port: +e.target.value })} className="rounded-md mt-1.5" /></div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              <Button data-testid="imap-connect-button" type="submit" disabled={imapBusy} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-11 flex-1 min-w-[200px]">
                {imapBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifica e connessione…</> : <><Mail className="h-4 w-4 mr-2" />Connetti e importa</>}
              </Button>
              <Button data-testid="mailbox-verify-button" type="button" onClick={verifyMailbox} disabled={verifying} variant="outline" className="rounded-md h-11">
                {verifying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Server className="h-4 w-4 mr-2" />}Prova connessione
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Prima del salvataggio verifichiamo automaticamente ricezione (IMAP) e invio (SMTP): se qualcosa non funziona, la casella non viene salvata. Vengono poi importate tutte le email della casella (fino a 500 più recenti). L'analisi AI parte quando apri la singola email. Quando approvi una bozza, l'email viene inviata tramite l'host SMTP indicato.</p>
            </>

            {pushInfo.accounts.filter((a) => !a.is_demo && !accounts.some((x) => x.id === a.id && x.is_demo)).length > 0 && (
              <div className="mt-1 pt-4 border-t border-border space-y-2.5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5"><Radio className="h-3.5 w-3.5 text-[#007BFF]" />Stato sincronizzazione (tempo reale)</div>
                {pushInfo.accounts.filter((a) => !a.is_demo && !accounts.some((x) => x.id === a.id && x.is_demo)).map((a) => {
                  const m = pushMeta(a);
                  return (
                    <div key={a.id} data-testid={`push-account-${a.id}`} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{a.email_address}</div>
                        <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
                          <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${m.dot} ${m.pulse ? "animate-pulse" : ""}`} />
                          <span className={m.text} data-testid={`push-state-${a.id}`}>{m.label}</span>
                          <span className="text-muted-foreground">· ultimo sync: {fmtSince(a.last_sync_at)}</span>
                        </div>
                        {m.label === "Errore" && a.push_message && (
                          <div className="text-[11px] text-red-500 truncate mt-0.5" title={a.push_message}>{a.push_message}</div>
                        )}
                      </div>
                      <span className={`text-xs px-3 py-1.5 rounded-md border shrink-0 ${m.badge}`} data-testid={`push-badge-${a.id}`}>{m.label.replace("…", "")}</span>
                    </div>
                  );
                })}
                <p className="text-[11px] text-muted-foreground">Le nuove email vengono importate e analizzate automaticamente non appena arrivano. Lo stato si aggiorna ogni 5 secondi.</p>
              </div>
            )}
          </form>
        </div>
        </div>
        {/* Colonna destra: caselle collegate e firme */}
        <div className="space-y-6">

        {/* Caselle collegate: modifica / elimina */}
        {accounts.length > 0 && (
          <div className="bg-card border border-border rounded-md p-6" data-testid="accounts-manager">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2"><Inbox className="h-5 w-5 text-[#007BFF]" /><h3 className="font-head text-lg font-semibold">Caselle collegate</h3></div>
              <Button data-testid="analyze-all-button" onClick={analyzeAll} disabled={analyzingAll} size="sm" variant="outline" className="rounded-md h-8 text-xs shrink-0" title="L'analisi AI è automatica; usa questo pulsante per ri-analizzare subito le email ancora in attesa">
                {analyzingAll ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}Analizza email in attesa
              </Button>
            </div>
            <div className="space-y-2.5">
              {accounts.map((a, idx) => (
                <div key={a.id} data-testid={`account-row-${a.id}`} className="border border-border rounded-md px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {accounts.length > 1 && (
                        <div className="flex flex-col shrink-0 -my-1">
                          <button data-testid={`account-move-up-${a.id}`} onClick={() => moveAccount(idx, -1)} disabled={idx === 0}
                            title="Sposta su" className="p-0.5 rounded text-muted-foreground hover:text-[#007BFF] hover:bg-secondary disabled:opacity-30 disabled:hover:bg-transparent transition-colors">
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button data-testid={`account-move-down-${a.id}`} onClick={() => moveAccount(idx, 1)} disabled={idx === accounts.length - 1}
                            title="Sposta giù" className="p-0.5 rounded text-muted-foreground hover:text-[#007BFF] hover:bg-secondary disabled:opacity-30 disabled:hover:bg-transparent transition-colors">
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      <div className="min-w-0">
                      <div className="font-medium text-sm truncate flex items-center gap-2">
                        {a.email_address}
                        {a.type === "gmail_oauth" && <span className="text-[10px] uppercase tracking-wider bg-[#EBF3FF] text-[#0056B3] px-1.5 py-0.5 rounded">Google</span>}
                        {a.is_demo && <span data-testid={`account-demo-badge-${a.id}`} className="text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Demo</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {a.is_demo ? "Casella dimostrativa · non riceve né invia email reali"
                          : a.type === "gmail_oauth" ? `Gmail API · ${a.last_sync_count ?? 0} email sincronizzate` : `${a.provider} · ${a.imap_host}:${a.imap_port}`}
                      </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {a.type === "gmail_oauth" && (
                        <button data-testid="resync-gmail-button" onClick={resyncGmail} disabled={resyncing} className="text-xs px-2.5 py-1.5 rounded-md border border-[#007BFF] text-[#007BFF] hover:bg-[#007BFF]/5 transition-colors duration-150 disabled:opacity-50" title={studioConfirmed ? "Scarica tutto lo storico della casella" : "Completa lo Studio per importare tutto lo storico (ora solo ultime 50 per cartella)"}>
                          {resyncing ? "Sincronizzo…" : (studioConfirmed ? "Importa tutto lo storico" : "Sincronizza")}
                        </button>
                      )}
                      {a.type !== "gmail_oauth" && !a.is_demo && (
                        <button data-testid={`reimport-account-${a.id}`} onClick={() => reimportAccount(a)} disabled={reimporting === a.id} className="text-xs px-2.5 py-1.5 rounded-md border border-[#007BFF] text-[#007BFF] hover:bg-[#007BFF]/5 transition-colors duration-150 disabled:opacity-50" title={studioConfirmed ? "Scarica tutto lo storico della casella" : "Completa lo Studio per importare tutto lo storico (ora solo ultime 50 per cartella)"}>
                          {reimporting === a.id ? "Avvio…" : (studioConfirmed ? "Importa tutto lo storico" : "Ri-importa")}
                        </button>
                      )}
                      {a.type !== "gmail_oauth" && !a.is_demo && <button data-testid={`edit-account-${a.id}`} onClick={() => openEdit(a)} className="p-2 rounded-md text-muted-foreground hover:text-[#007BFF] hover:bg-secondary transition-colors duration-150" title="Modifica"><Pencil className="h-4 w-4" /></button>}
                      <button data-testid={`delete-account-${a.id}`} onClick={() => setDelAcc(a)} className="p-2 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors duration-150" title="Elimina"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {a.last_sync_error && (
                    <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 break-words" data-testid="gmail-sync-error">
                      Ultimo errore sincronizzazione: {a.last_sync_error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Firma per casella (usata dall'AI nelle bozze) */}
        <SignaturePerMailbox />
        </div>
        </div>
      </div>

      <Dialog open={!!editAcc} onOpenChange={(o) => !o && setEditAcc(null)}>
        <DialogContent data-testid="edit-account-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica casella</DialogTitle>
            <DialogDescription className="text-xs">Lascia vuota la password per mantenere quella attuale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Indirizzo email</Label>
              <Input data-testid="edit-email" type="email" value={editForm.email_address} onChange={(e) => setEditForm({ ...editForm, email_address: e.target.value })} className="rounded-md mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Password (opzionale)</Label>
              <PasswordInput data-testid="edit-password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="•••••••• (invariata)" className="rounded-md mt-1.5" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Host IMAP (ricezione)</Label>
                <Input data-testid="edit-imap-host" value={editForm.imap_host} onChange={(e) => setEditForm({ ...editForm, imap_host: e.target.value })} className="rounded-md mt-1.5" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Porta</Label>
                <Input data-testid="edit-imap-port" type="number" value={editForm.imap_port} onChange={(e) => setEditForm({ ...editForm, imap_port: e.target.value })} className="rounded-md mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Host SMTP (invio)</Label>
                <Input data-testid="edit-smtp-host" value={editForm.smtp_host} onChange={(e) => setEditForm({ ...editForm, smtp_host: e.target.value })} placeholder="smtp.tuoprovider.it" className="rounded-md mt-1.5" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Porta</Label>
                <Input data-testid="edit-smtp-port" type="number" value={editForm.smtp_port} onChange={(e) => setEditForm({ ...editForm, smtp_port: e.target.value })} className="rounded-md mt-1.5" /></div>
            </div>
            <p className="text-[11px] text-muted-foreground">L'host SMTP è usato per inviare le email e le risposte. Lascia la password vuota per mantenere quella attuale (vale sia per ricezione che invio).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAcc(null)} className="rounded-md">Annulla</Button>
            <Button data-testid="save-account-button" onClick={saveEdit} disabled={savingEdit} className="rounded-md bg-[#007BFF] hover:bg-[#0056B3]">
              {savingEdit ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delAcc} onOpenChange={(o) => !o && setDelAcc(null)}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la casella?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <span className="font-semibold text-foreground">{delAcc?.email_address}</span> e tutte le email importate da questa casella. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-account">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-account" onClick={(e) => { e.preventDefault(); confirmDelete(); }} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
